import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable FormField component for testimonial forms
 * Handles various input types and accessibility features
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered form field
 */
export const FormField = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  disabled = false,
  required = false,
  sanitize = false,
  placeholder = '',
  ariaLabel,
  className = 'testi-header-field',
  defaultValue,
  children,
  readOnly = false,
  min,
  max,
  onBlur,
}) => {
  // Handle sanitized inputs for social media handles
  const handleSanitizedChange = useCallback(
    e => {
      if (sanitize && onChange) {
        const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9.@_]/g, '');
        e.target.value = sanitizedValue;
      }
      onChange && onChange(e);
    },
    [onChange, sanitize]
  );

  // Determine if we should use controlled or uncontrolled approach
  const isControlled = value !== undefined;

  // Determine if we should render a select element or input
  const renderSelect = () => (
    <div className="testi-header-select-wrapper">
      <select
        id={id}
        name={id}
        className="testi-header-select"
        onChange={onChange}
        onBlur={onBlur}
        aria-label={ariaLabel || label}
        aria-required={required}
        disabled={disabled}
        {...(isControlled ? { value } : { defaultValue })}
        required={required}>
        {children}
      </select>
      <span className="testi-header-select-icon" aria-hidden="true"></span>
    </div>
  );

  // Render input element with appropriate attributes
  const renderInput = () => (
    <input
      type={type}
      id={id}
      name={id}
      className="testi-header-input"
      onChange={handleSanitizedChange}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-label={ariaLabel || label}
      aria-required={required}
      disabled={disabled}
      required={required}
      readOnly={readOnly}
      {...(isControlled ? { value } : { defaultValue })}
      min={min}
      max={max}
    />
  );

  return (
    <div className={className}>
      <label className="testi-header-label" htmlFor={id}>
        <span>
          <b>{label}</b>
          {!required && required !== undefined && (
            <span className="visually-hidden"> (Optional)</span>
          )}
        </span>
      </label>
      {type === 'select' ? renderSelect() : renderInput()}
    </div>
  );
};

FormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  sanitize: PropTypes.bool,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  readOnly: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  onBlur: PropTypes.func,
};
